import { Button, Col, Spinner } from 'react-bootstrap'
import { camelCase } from 'lodash'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { ThunkDispatch, UnknownAction } from '@reduxjs/toolkit'
import { useInterval, ConfirmationModal } from '../../common'
import { isLayoutEngineInProgressStatus } from '../../article'
import { cancelLayout, fetchLayoutableStatus } from '../articleAPI'
import { closeModal, openModal } from '../articleActions'
import type { StatusValue } from '../../common'
import type { FormConfig } from '../../common/form/CustomForm'

type SpinnerLayoutable = {
  status?: StatusValue | null
  id?: string
  layoutableType: string
  lockVersion?: number | null
}

type SpinnerProps = {
  delay: number
  waitedTime: number
  setWaitedTime: (time: number) => void
  dispatch: ThunkDispatch<unknown, unknown, UnknownAction>
  cancelInProgress: boolean
  setCancelInProgress: (v: boolean) => void
}

type Props = {
  layoutable: SpinnerLayoutable
  enumValues: { statuses: Record<string, string> }
  spinnerProps: SpinnerProps
  openedModal: string | null
}

export const LayoutableStatusSpinner = ({ layoutable, enumValues, spinnerProps, openedModal }: Props) => {
  const { delay, waitedTime, setWaitedTime, dispatch, cancelInProgress, setCancelInProgress } = spinnerProps
  const status = layoutable?.status
  const shouldPoll = isLayoutEngineInProgressStatus(status as StatusValue)
  const variant = shouldPoll ? 'primary' : 'outline-success'
  const { id: layoutableId, layoutableType } = layoutable
  useInterval(
    () => {
      if (shouldPoll) {
        dispatch(fetchLayoutableStatus({ layoutableId, layoutableType }))
      }
      if (delay > 0) {
        setWaitedTime(waitedTime + delay)
      }
    },
    shouldPoll ? delay : null
  )
  if (!shouldPoll) {
    return (
      <Col sm='auto' className='px-0 layoutable-status-spinner'>
        <Button variant={variant} size='sm' disabled>
          {enumValues.statuses[camelCase(layoutable?.status ?? undefined)]}
        </Button>
      </Col>
    )
  }

  const handleCancelLayoutSubmit = () => {
    dispatch(
      cancelLayout({
        layoutableType,
        layoutableId,
        lockVersion: layoutable?.lockVersion
      })
    )
  }
  const statusString = (
    <>
      <Spinner as='span' animation='border' role='status' aria-hidden='true' size='sm' /> 割り付け実行中...
    </>
  )
  const modalSetting = {
    name: 'cancel-layout',
    form: {
      initialValues: {
        layoutableId,
        layoutableType
      },
      handleSubmit: handleCancelLayoutSubmit,
      props: {
        title: '割り付けキャンセル',
        submitButtonText: '割り付けをキャンセルする',
        onClickCancel: () => dispatch(closeModal()),
        ConfirmationBody: () => {
          return <>割り付けをキャンセルしますか？</>
        }
      }
    } as unknown as FormConfig
  }
  return (
    <Col sm='auto' className='px-0 layoutable-status-spinner'>
      <Button variant={variant} size='sm' disabled>
        {statusString}
      </Button>
      {layoutableType === 'Page' && (
        <>
          <ConfirmationModal
            key='cancel-layout-modal'
            name={modalSetting.name}
            form={modalSetting.form}
            openedModal={openedModal}
          />
          <Button
            variant='outline-danger'
            size='sm'
            disabled={cancelInProgress}
            onClick={(e) => {
              e.preventDefault()
              setCancelInProgress(true)
              dispatch(openModal('cancel-layout'))
            }}
          >
            <FontAwesomeIcon icon='ban' />
            割り付けキャンセル
          </Button>
        </>
      )}
    </Col>
  )
}
